import { Order, OrderItem } from "@/types";
import { http, httpForm } from "../utils/http-common";
import { useUploadsStore } from "../store/uploads.store";
import { Upload } from "../types";
import { File } from "@/interfaces";
import { storeToRefs } from "pinia";
import axios from "axios";

export interface PreviewUrlEntry {
  url: string;
  expiresAt: number;
}

export function getPreviewUrls(previewFileIds: number[]): Promise<{ urls: Record<string, PreviewUrlEntry> }> {
  return http.post("/files/previews/urls", { previewFileIds }).then((res) => res.data);
}

export async function uploadFilesInBackgroud(newOrder: Order, orderItems: OrderItem[]) {
  // Resolve store inside the action — module-level useUploadsStore() breaks when this
  // file is imported before app.use(pinia) (e.g. files.store → preview prefetch).
  const upladsStore = useUploadsStore();
  const { uploads } = storeToRefs(upladsStore);

  for (const item of newOrder.orderItems) {
    const orderItem = orderItems.find((orderItem) => orderItem.line === item.line && (orderItem.isNew || orderItem.isChangingFile));

    if (orderItem && item.currentFile?.metadata?.presignedURL && !(newOrder.layout && newOrder.preprocessLayout && !item.isAttachment)) {
      const file = orderItem.uploadedFile;
      const newUpload: Upload = { uuid: item.currentFile.uuid, fileName: file.name, order: newOrder, orderItem: orderItem, loaded: 0, total: file.size, status: "uploading", startedDate: new Date() };
      uploads.value.push(newUpload);

      axios
        .put(item.currentFile.metadata.presignedURL, file, {
          headers: {
            "Content-Type": file.type,
          },
          onUploadProgress: function (progressEvent) {
            const foundUpload = uploads.value.find((upload: Upload) => upload.uuid === item.currentFile?.uuid);
            console.log({ progressEvent, foundUpload });
            if (foundUpload) foundUpload.loaded = progressEvent.loaded;
          },
        })
        .then(() => {
          http.get(`/engine/generateFileInfo/${item.currentFile?.id}?new=true`);
          const foundUpload = uploads.value.find((upload: Upload) => upload.order.id === newOrder.id && upload.orderItem.line === item.line);
          if (foundUpload) {
            foundUpload.loaded = foundUpload.total;
            foundUpload.status = "done";
          }
        })
        .catch((e) => {
          console.log(e);
          newUpload.status = "error";
        });
    }
  }
}

export async function uploadFiles(newOrder: Order, orderItems: OrderItem[], onProgress: (arg1: number) => void): Promise<void[]> {
  const totalSize = orderItems.filter((item) => item.isChangingFile || item.isNew).reduce((acc: number, item: OrderItem) => acc + item.uploadedFile?.size || 0, 0);
  const percentagePerItem: number[] = [];

  const promises = newOrder.orderItems.map(async (item: OrderItem) => {
    const config = {
      onUploadProgress: function (progressEvent: any) {
        const percentage = (progressEvent.loaded * 100 * (progressEvent.total / totalSize)) / progressEvent.total;
        percentagePerItem[item.line] = percentage;
        const totalPercentage = Math.round(percentagePerItem.reduce((acc: number, item: number) => acc + item, 0));
        onProgress(totalPercentage);
      },
    };
    const formData = new FormData();

    const orderItem = orderItems.find((orderItem) => orderItem.line === item.line && (orderItem.isNew || orderItem.isChangingFile));
    if (!orderItem) return;
    const file = orderItem.uploadedFile;
    formData.append("orderItemId", JSON.stringify(item.id));
    formData.append("file", file);
    const response = await httpForm.postForm(`/files/upload`, formData, config);
    return response.data;
  });

  const results = await Promise.all(promises);
  return results;
}

export function getFile(fileId: number): Promise<File> {
  return http.get(`/files/${fileId}`).then((res) => {
    return res.data.data;
  });
}

export function uploadPreviewForFile(fileId: number, preview: Blob, filename = "preview.jpg"): Promise<File> {
  const formData = new FormData();
  formData.append("file", preview, filename);
  return httpForm.postForm(`/files/${fileId}/uploadPreview`, formData).then((res) => {
    return res.data.data;
  });
}
